import { useMemo } from "react";
import type { NewsletterCard } from "@shared/newsletterTypes";
import { previewBodyHtml, PREVIEW_LOGO_SRC } from "./previewHtml";

/**
 * Live preview of an issue, rendered as you type.
 *
 * Calls the SAME renderNewsletter the send path calls. That is the whole point:
 * a preview built from a second, "close enough" template is a preview that can
 * disagree with what actually ships, and this email goes to the entire buyer
 * network. renderNewsletter lives in shared/ precisely so the browser can run
 * it, so there is no server round trip and nothing to fall out of sync.
 *
 * Two honest differences from a real send, both unavoidable here and neither
 * affecting layout or copy:
 *   1. Per-recipient links are placeholders. The real ones are HMAC-signed at
 *      send time, per recipient, and minting one here would mean either
 *      shipping the secret to the browser or signing a token for a recipient
 *      who does not exist. They render as inert "#" links instead.
 *   2. The sending mailbox's signature block and the firm compliance footer
 *      are appended by composeHtml() on the server at send time, so they are
 *      not shown. A test send is the way to see those. (A custom-HTML issue
 *      gets the footer but no signature.)
 *
 * A custom-HTML issue previews its own letter verbatim, with the same link
 * neutralising and the inline logo swapped for a data URL of the same image.
 *
 * Rendered into a sandboxed iframe rather than inline: the body is a full HTML
 * document with its own inline CSS, and dropping that into the page would let
 * it inherit (and fight with) the app's styles, showing something neither the
 * composer nor the recipient would ever see.
 */
export function LivePreview({
  intro,
  cards,
  customHtml = null,
  className,
}: {
  intro: string;
  cards: NewsletterCard[];
  customHtml?: string | null;
  className?: string;
}) {
  const srcDoc = useMemo(() => {
    // baseUrl is irrelevant here since every link is neutralised, but
    // renderNewsletter requires one and a real origin keeps the markup honest.
    const html = previewBodyHtml({
      intro,
      cards,
      customHtml,
      baseUrl: window.location.origin,
      logoSrc: PREVIEW_LOGO_SRC,
    });

    return `<!doctype html><html><head><meta charset="utf-8">
      <style>body{margin:0;padding:16px;background:#fff}</style>
      </head><body>${html}</body></html>`;
  }, [intro, cards, customHtml]);

  return (
    <div className={className}>
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-sm font-semibold">Preview</h2>
        <p className="text-[11px] text-muted-foreground">
          {customHtml === null ? "Signature and footer are added when it sends" : "Compliance footer is added when it sends"}
        </p>
      </div>
      <iframe
        title="Newsletter preview"
        sandbox=""
        srcDoc={srcDoc}
        className="w-full h-[640px] border rounded-lg bg-white"
      />
    </div>
  );
}
